import { useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { usePlans, useUpdatePlan } from "../../hooks/useSubscribtionDashboard";
import {
  DEFAULT_TEAM_PRICING_CONFIG,
  getPlanTeamPricingConfig,
  getTeamPricingSummary,
  normalizeWholeNumberInput,
  TeamPricingActions,
  TeamPricingCard,
  TeamPricingHeader,
  TeamPricingInfoCard,
  TeamSeatRulesCard,
} from "../../components/subscribtionDashboard/teamPricing";

export default function TeamPricingConfig({ showPlansTable = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const initialPlan = location?.state?.plan || null;
  const initialPlanId =
    location?.state?.planId ??
    location?.state?.plan?.id ??
    location?.state?.plan?._id ??
    null;

  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedPlanId, setSelectedPlanId] = useState(initialPlanId);
  const [config, setConfig] = useState(DEFAULT_TEAM_PRICING_CONFIG);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);

  const offset = page * limit;
  const { data: plansData } = usePlans({
    limit,
    offset,
  });

  const { mutate: updatePlan, isPending } = useUpdatePlan();

  useEffect(() => {
    if (showPlansTable) return;
    if (!initialPlan || selectedPlan) return;

    setSelectedPlan(initialPlan);
    setSelectedPlanId(initialPlan.id ?? initialPlan._id ?? initialPlanId);
  }, [initialPlan, initialPlanId, selectedPlan, showPlansTable]);

  const plansList = useMemo(() => plansData?.data?.data ?? [], [plansData]);

  useEffect(() => {
    if (selectedPlan || !selectedPlanId || plansList.length === 0) return;

    const match = plansList.find((plan) => plan._id === selectedPlanId);
    if (match) {
      setSelectedPlan(match);
    }
  }, [plansList, selectedPlan, selectedPlanId]);

  useEffect(() => {
    if (!selectedPlan) return;
    setConfig(getPlanTeamPricingConfig(selectedPlan));
  }, [selectedPlan]);

  const handleChange = (field, value) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
  };

  const handlePriceChange = (event) => {
    handleChange(
      "pricePerTeamMember",
      parseFloat(event.target.value) || 0,
    );
  };

  const handleTrialDurationChange = (event) => {
    handleChange(
      "trialDurationDays",
      normalizeWholeNumberInput(event.target.value),
    );
  };

  const handleMinimumSeatsChange = (event) => {
    handleChange(
      "minimumSeatCount",
      normalizeWholeNumberInput(event.target.value),
    );
  };

  const handleMaximumSeatsChange = (event) => {
    handleChange(
      "maximumSeatCount",
      normalizeWholeNumberInput(event.target.value),
    );
  };

  const handleSave = () => {
    if (!selectedPlan) return;

    updatePlan(
      {
        id: selectedPlan.id ?? selectedPlan._id,
        pricePerTeamMember: Number(config.pricePerTeamMember),
        trialDurationDays: Number(config.trialDurationDays),
        minimumSeatCount: Number(config.minimumSeatCount),
        maximumSeatCount: Number(config.maximumSeatCount),
      },
      {
        onSuccess: () => {
          navigate("/subscritionDashboard/plans");
        },
      },
    );
  };

  const summary = getTeamPricingSummary(config);
  const isFormDisabled = !selectedPlan || isPending;

  return (
    <Box>
      {!selectedPlan && (
        <Box className="mb-6">
          <Typography variant="body2" className="text-slate-600">
            {showPlansTable
              ? "Select a plan above to configure team pricing."
              : "No plan selected for team pricing."}
          </Typography>
        </Box>
      )}

      {selectedPlan && (
        <>
          <TeamPricingHeader
            showBackButton={!showPlansTable}
            onBack={() => navigate(-1)}
            planName={selectedPlan.name}
          />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
              gap: 3,
            }}
          >
            <TeamPricingCard
              config={config}
              disabled={isFormDisabled}
              onPriceChange={handlePriceChange}
              onTrialDurationChange={handleTrialDurationChange}
            />
            <TeamSeatRulesCard
              config={config}
              summary={summary}
              disabled={isFormDisabled}
              onMinimumSeatsChange={handleMinimumSeatsChange}
              onMaximumSeatsChange={handleMaximumSeatsChange}
            />
            <TeamPricingInfoCard />
          </Box>

          <TeamPricingActions
            disabled={isFormDisabled}
            isPending={isPending}
            onSave={handleSave}
          />
        </>
      )}
    </Box>
  );
}
